import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface ContactItem {
  id: number
  display_name: string
  category: string | null
  info: string
  notes: string | null
  added_by: string | null
  added_on: string | null
  status: number
  can_edit: boolean
  can_delete: boolean
}

interface Props {
  case_record: CaseRecord
  contacts: ContactItem[]
  can_add: boolean
}

export default function Index({ case_record, contacts, can_add }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Contacts", href: `/cases/${case_record.id}/contacts` },
  ]

  const basePath = `/cases/${case_record.id}/contacts`

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this contact?")) {
      router.delete(`${basePath}/${id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Contacts - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Contacts for {case_record.display_name}
          </h1>
          {can_add && (
            <Link
              href={`${basePath}/new`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Add Contact
            </Link>
          )}
        </div>

        {contacts.length > 0 ? (
          <div className="space-y-3">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border p-4"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold">
                      <span className="text-hpi-dark-gray">
                        {contact.category}
                      </span>
                      : {contact.info}
                    </div>
                    {contact.notes && (
                      <div className="text-hpi-med-gray mt-1 text-sm whitespace-pre-line">
                        {contact.notes}
                      </div>
                    )}
                    {contact.added_by && (
                      <div className="text-hpi-med-gray mt-1 text-xs">
                        Added by {contact.added_by}
                        {contact.added_on && ` on ${contact.added_on}`}
                      </div>
                    )}
                  </div>
                  {(contact.can_edit || contact.can_delete) && (
                    <div className="flex gap-2 whitespace-nowrap">
                      {contact.can_edit && (
                        <Link
                          href={`${basePath}/${contact.id}/edit`}
                          className="text-hpi-active-blue text-sm hover:underline"
                        >
                          Edit
                        </Link>
                      )}
                      {contact.can_delete && (
                        <button
                          onClick={() => handleDelete(contact.id)}
                          className="text-hpi-red text-sm hover:underline"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-hpi-med-gray">No contacts found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
